import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Pagination,
  Alert,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { API_URLS } from "../../config/apiUrls";
import SorteioEditModal from "../SorteioEditModal";

export type SystemType = "ANUNCIO360" | "ESCALA11";

interface Ticket {
  id: string;
  number: number;
  createdAt: string;
}

interface Participant {
  userId: string;
  userName: string;
  userEmail: string;
  tickets: Ticket[];
  ticketCount: number;
  participationDate: string;
}

// Nova interface para o ganhador, conforme a API de busca
interface WinnerData {
  ticketNumber: number;
  user: {
    id: string;
    name: string;
    email: string;
    phone: string;
    city: {
      id: string;
      name: string;
      uf: {
        id: number;
        name: string;
        uf: string;
      };
    };
  };
  ticket: {
    id: string;
    number: number;
    createdAt: string;
  };
  draw: {
    id: string;
    title: string;
  };
}

interface SorteioProps {
  id: string;
  title: string;
  description: string;
  startDate: string;
  drawDate: string;
  displayEndDate: string;
  minNumber: number;
  maxNumber: number;
  ticketsPerUser: number;
  participationRule: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  tickets: any[];
  participantsCount: number;
  availableNumbers: number;
  totalPrize: number;
  prizePercentage: number;
  prizeInfo: {
    basePrize: number;
    currentPrize: number;
    totalPrizeIncrease: number;
    approvedAdsCount: number;
    totalApprovedAdsValue: number;
    hasDynamicPrize: boolean;
    hasFixedPrize: boolean;
    hasPercentageOnly: boolean;
  };
  role: "MASTER" | "PARTNER" | "ANALYST";
  token: string;
}

interface SorteioDetalhesModalProps {
  open: boolean;
  onClose: () => void;
  sorteio: SorteioProps;
  token: string;
  onSorteioUpdated: () => void;
  system: SystemType;
}

interface PrizeStats {
  drawId: string;
  basePrize: number;
  prizePercentage: number;
  currentPrize: number;
  totalPrizeIncrease: number;
  approvedAdsCount: number;
  totalApprovedAdsValue: number;
  averagePrizeIncreasePerAd: number;
  projectedFinalPrize: number;
  lastUpdated: string;
}

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: 700, md: 800 },
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  maxHeight: "90vh",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
};

const SorteioDetalhesModal: React.FC<SorteioDetalhesModalProps> = ({
  open,
  onClose,
  sorteio,
  token,
  onSorteioUpdated,
  system,
}) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loadingParticipants, setLoadingParticipants] =
    useState<boolean>(false);
  const [participantsError, setParticipantsError] = useState<string | null>(
    null
  );
  const [participantsPage, setParticipantsPage] = useState<number>(1);
  const [participantsLimit] = useState<number>(10);
  const [totalParticipantsPages, setTotalParticipantsPages] =
    useState<number>(0);

  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [ticketNumber, setTicketNumber] = useState<string>("");
  const [winner, setWinner] = useState<WinnerData | null>(null);
  const [loadingWinner, setLoadingWinner] = useState<boolean>(false);
  const [winnerError, setWinnerError] = useState<string | null>(null);

  const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] =
    useState<boolean>(false);

  // --- Novos estados para as estatísticas do prêmio ---
  const [prizeStats, setPrizeStats] = useState<PrizeStats | null>(null);
  const [loadingPrizeStats, setLoadingPrizeStats] = useState<boolean>(false);
  const [prizeStatsError, setPrizeStatsError] = useState<string | null>(null);

  const formattedDrawDate = new Date(sorteio.drawDate).toLocaleDateString();
  const formattedStartDate = new Date(sorteio.startDate).toLocaleDateString();
  const formattedDisplayEndDate = new Date(
    sorteio.displayEndDate
  ).toLocaleDateString();
  const formattedCreatedAt = new Date(sorteio.createdAt).toLocaleDateString();
  const formattedUpdatedAt = new Date(sorteio.updatedAt).toLocaleDateString();

  const escala11ButtonStyleActive = {
    backgroundColor: "#008870",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#005848ff",
    },
  };

  const escala11ButtonStyleOutline = {
    color: "#008870",
    border: "1px solid #008870",
    "&:hover": {
      color: "#005848ff",
      border: "1px solid #005848ff",
    },
  };

  const isEscala11 = system === "ESCALA11";

  const fetchParticipants = useCallback(
    async (page: number) => {
      setLoadingParticipants(true);
      setParticipantsError(null);
      try {
        const response = await fetch(
          `${API_URLS.sorteios}/${sorteio.id}/participants?page=${page}&limit=${participantsLimit}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Erro ao buscar participantes: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();
        setParticipants(data.participants);
        setTotalParticipantsPages(Math.ceil(data.total / participantsLimit));
      } catch (err) {
        if (err instanceof Error) {
          setParticipantsError(err.message);
        } else {
          setParticipantsError(
            "Ocorreu um erro desconhecido ao carregar participantes."
          );
        }
      } finally {
        setLoadingParticipants(false);
      }
    },
    [sorteio.id, participantsLimit, token]
  );

  const fetchWinner = useCallback(async () => {
    setLoadingWinner(true);
    setWinnerError(null);
    setWinner(null);

    if (!ticketNumber) {
      setWinnerError("Por favor, insira um número de bilhete.");
      setLoadingWinner(false);
      return;
    }

    const numTicket = parseInt(ticketNumber);
    if (isNaN(numTicket) || numTicket <= 0) {
      setWinnerError("Número do bilhete inválido. Insira um número positivo.");
      setLoadingWinner(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_URLS.sorteios}/${sorteio.id}/participants/filter?ticketNumber=${numTicket}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          setWinnerError(
            `Nenhum participante encontrado com o bilhete ${numTicket}.`
          );
        } else {
          const errorData = await response.json();
          throw new Error(
            errorData.message ||
              `Erro ao buscar ganhador: ${response.status} ${response.statusText}`
          );
        }
      } else {
        const data = await response.json();
        // A API retorna o ganhador dentro de um objeto 'winner'
        setWinner(data.winner || null);
        if (!data.winner) {
          setWinnerError(
            `Nenhum ganhador encontrado para o bilhete ${numTicket}.`
          );
        }
      }
    } catch (err) {
      if (err instanceof Error) {
        setWinnerError(err.message);
      } else {
        setWinnerError("Ocorreu um erro desconhecido ao buscar o ganhador.");
      }
    } finally {
      setLoadingWinner(false);
    }
  }, [sorteio.id, ticketNumber, token]);

  const fetchPrizeStats = useCallback(async () => {
    setLoadingPrizeStats(true);
    setPrizeStatsError(null);
    try {
      const response = await fetch(
        `${API_URLS.sorteios}/${sorteio.id}/prize-stats`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Erro ao buscar estatísticas: ${response.status} ${response.statusText}`
        );
      }

      const data: PrizeStats = await response.json();
      setPrizeStats(data);
    } catch (err) {
      if (err instanceof Error) {
        setPrizeStatsError(err.message);
      } else {
        setPrizeStatsError(
          "Ocorreu um erro desconhecido ao carregar as estatísticas."
        );
      }
    } finally {
      setLoadingPrizeStats(false);
    }
  }, [sorteio.id, token]);

  useEffect(() => {
    if (open && currentTab === 1) {
      // Lembre-se de ajustar o índice da aba
      fetchParticipants(participantsPage);
    }
    // Adicione a chamada para a nova aba de estatísticas
    if (open && currentTab === 2) {
      // Use o próximo índice disponível
      fetchPrizeStats();
    }
    if (!open) {
      setTicketNumber("");
      setWinner(null);
      setWinnerError(null);
      setOpenConfirmDeleteDialog(false);
    }
  }, [open, currentTab, participantsPage, fetchParticipants, fetchPrizeStats]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    if (newValue === 1 && !participants.length && !loadingParticipants) {
      setParticipantsPage(1);
      fetchParticipants(1);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const handleParticipantsPageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setParticipantsPage(value);
  };

  const handleOpenEditModal = () => {
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    onSorteioUpdated();
  };

  // --- Funções para o diálogo de confirmação de exclusão ---
  const handleOpenConfirmDeleteDialog = () => {
    setOpenConfirmDeleteDialog(true);
  };

  const handleCloseConfirmDeleteDialog = () => {
    setOpenConfirmDeleteDialog(false);
    setDeleteError(null); // Clear any previous delete errors when closing dialog
  };

  const handleConfirmDelete = async () => {
    handleCloseConfirmDeleteDialog(); // Close the confirmation dialog
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const response = await fetch(`${API_URLS.sorteios}/${sorteio.id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erro ao deletar sorteio.");
      }

      onClose(); // Fecha o modal de detalhes
      onSorteioUpdated(); // Notifica o componente pai para atualizar a lista
    } catch (err) {
      if (err instanceof Error) {
        setDeleteError(err.message);
      } else {
        setDeleteError("Ocorreu um erro desconhecido ao deletar o sorteio.");
      }
    } finally {
      setIsDeleting(false);
    }
  };
  // --- Fim das funções para o diálogo de confirmação de exclusão ---

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="sorteio-modal-title"
      aria-describedby="sorteio-modal-description"
    >
      <Box sx={style}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography id="sorteio-modal-title" variant="h5" component="h2">
            {sorteio.title}
          </Typography>
          <IconButton onClick={onClose} aria-label="fechar">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="abas de detalhes do sorteio"
          >
            <Tab label="Detalhes" />
            <Tab label={`Participantes (${sorteio.participantsCount})`} />
            <Tab label="Estatísticas do Prêmio" /> {/* Nova Aba */}
          </Tabs>
        </Box>

        {currentTab === 0 && ( // Aba de Detalhes
          <Box sx={{ mt: 2, flexGrow: 1, overflowY: "auto", pr: 1 }}>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
              Descrição:{" "}
              <Box sx={{ display: "inline", fontWeight: "regular" }}>
                {sorteio.description}
              </Box>
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
              Data do Sorteio:{" "}
              <Box
                sx={{
                  display: "inline-block", // Para o quadrado se ajustar ao conteúdo
                  backgroundColor: "#c9daf8", // Cor de fundo (pode mudar conforme sua necessidade)
                  color: "#073763", // Cor do texto
                  padding: "2px 4px", // Espaçamento dentro do quadrado
                  borderRadius: "4px", // Arredondar os cantos (opcional)
                  textAlign: "center", // Centralizar o texto
                  fontWeight: "bold", // Deixar o texto em negrito
                }}
              >
                {formattedDrawDate}
              </Box>
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
              Data Inicial:{" "}
              <Box
                sx={{
                  display: "inline-block", // Para o quadrado se ajustar ao conteúdo
                  backgroundColor: "#c9daf8", // Cor de fundo (pode mudar conforme sua necessidade)
                  color: "#073763", // Cor do texto
                  padding: "2px 4px", // Espaçamento dentro do quadrado
                  borderRadius: "4px", // Arredondar os cantos (opcional)
                  textAlign: "center", // Centralizar o texto
                  fontWeight: "bold", // Deixar o texto em negrito
                }}
              >
                {formattedStartDate}
              </Box>
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
              Data Final:{" "}
              <Box
                sx={{
                  display: "inline-block", // Para o quadrado se ajustar ao conteúdo
                  backgroundColor: "#c9daf8", // Cor de fundo (pode mudar conforme sua necessidade)
                  color: "#073763", // Cor do texto
                  padding: "2px 4px", // Espaçamento dentro do quadrado
                  borderRadius: "4px", // Arredondar os cantos (opcional)
                  textAlign: "center", // Centralizar o texto
                  fontWeight: "bold", // Deixar o texto em negrito
                }}
              >
                {formattedDisplayEndDate}
              </Box>
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
              Regra de Participação:{" "}
              <Box sx={{ display: "inline", fontWeight: "regular" }}>
                {sorteio.participationRule}
              </Box>
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
              Números Mínimo/Máximo:{" "}
              <Box sx={{ display: "inline", fontWeight: "regular" }}>
                {sorteio.minNumber} - {sorteio.maxNumber}
              </Box>
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
              Números por Usuário:{" "}
              <Box sx={{ display: "inline", fontWeight: "regular" }}>
                {sorteio.ticketsPerUser}
              </Box>
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
              Número de Participantes:{" "}
              <Box sx={{ display: "inline", fontWeight: "regular" }}>
                {sorteio.participantsCount}
              </Box>
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
              Números Disponíveis:{" "}
              <Box sx={{ display: "inline", fontWeight: "regular" }}>
                {sorteio.availableNumbers}
              </Box>
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
              Criado em:{" "}
              <Box sx={{ display: "inline", fontWeight: "regular" }}>
                {formattedCreatedAt}
              </Box>
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: "bold" }}>
              Última Atualização:{" "}
              <Box sx={{ display: "inline", fontWeight: "regular" }}>
                {formattedUpdatedAt}
              </Box>
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
              Número de Anúncios aprovados no período:{" "}
              <Box
                sx={{
                  display: "inline-block",
                  backgroundColor: "#fff2cc",
                  color: "#7f6000",
                  padding: "2px 4px",
                  borderRadius: "4px",
                  textAlign: "center",
                  fontWeight: "bold",
                }}
              >
                {sorteio.prizeInfo.approvedAdsCount}
              </Box>
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
              Valor dos Anúncios Aprovados:{" "}
              <Box
                sx={{
                  display: "inline-block",
                  backgroundColor: "#fff2cc",
                  color: "#7f6000",
                  padding: "2px 4px",
                  borderRadius: "4px",
                  textAlign: "center",
                  fontWeight: "bold",
                }}
              >
                {formatCurrency(sorteio.prizeInfo.totalApprovedAdsValue)}
              </Box>
            </Typography>
            <Divider sx={{ my: 2 }} />
            {/* Validação do tipo de sorteio */}
            {sorteio.prizeInfo.hasDynamicPrize && (
              <Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
                  Valor Base do Prêmio:{" "}
                  <Box
                    sx={{
                      display: "inline-block",
                      backgroundColor: "#d9ead3",
                      color: "#274e13",
                      padding: "2px 4px",
                      borderRadius: "4px",
                      textAlign: "center",
                      fontWeight: "bold",
                    }}
                  >
                    {formatCurrency(sorteio.prizeInfo.basePrize)}
                  </Box>
                </Typography>
                {sorteio.prizeInfo.totalPrizeIncrease != null && (
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: "bold" }}
                  >
                    Valor Adicionado ao prêmio:{" "}
                    <Box
                      sx={{
                        display: "inline-block",
                        backgroundColor: "#d9ead3",
                        color: "#274e13",
                        padding: "2px 4px",
                        borderRadius: "4px",
                        textAlign: "center",
                        fontWeight: "bold",
                      }}
                    >
                      {formatCurrency(sorteio.prizeInfo.totalPrizeIncrease)}
                    </Box>
                  </Typography>
                )}
                <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
                  Valor Atual do prêmio:{" "}
                  <Box
                    sx={{
                      display: "inline-block",
                      backgroundColor: "#d9ead3",
                      color: "#274e13",
                      padding: "2px 4px",
                      borderRadius: "4px",
                      textAlign: "center",
                      fontWeight: "bold",
                    }}
                  >
                    {formatCurrency(sorteio.prizeInfo.currentPrize)}
                  </Box>
                </Typography>
              </Box>
            )}

            {sorteio.prizeInfo.hasPercentageOnly && (
              <Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
                  Valor Adicionado ao prêmio:{" "}
                  <Box
                    sx={{
                      display: "inline-block",
                      backgroundColor: "#d9ead3",
                      color: "#274e13",
                      padding: "2px 4px",
                      borderRadius: "4px",
                      textAlign: "center",
                      fontWeight: "bold",
                    }}
                  >
                    {formatCurrency(sorteio.prizeInfo.totalPrizeIncrease)}
                  </Box>
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
                  Valor Atual do prêmio:{" "}
                  <Box
                    sx={{
                      display: "inline-block",
                      backgroundColor: "#d9ead3",
                      color: "#274e13",
                      padding: "2px 4px",
                      borderRadius: "4px",
                      textAlign: "center",
                      fontWeight: "bold",
                    }}
                  >
                    {formatCurrency(sorteio.prizeInfo.currentPrize)}
                  </Box>
                </Typography>
              </Box>
            )}

            {sorteio.prizeInfo.hasFixedPrize && (
              <Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
                  Valor do Prêmio:{" "}
                  <Box
                    sx={{
                      display: "inline-block",
                      backgroundColor: "#d9ead3",
                      color: "#274e13",
                      padding: "2px 4px",
                      borderRadius: "4px",
                      textAlign: "center",
                      fontWeight: "bold",
                    }}
                  >
                    {formatCurrency(sorteio.prizeInfo.basePrize)}
                  </Box>
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            {/* --- Seção de Busca do Ganhador --- */}
            <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
              Localizar Ganhador por Bilhete
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
              <TextField
                label="Número do Bilhete"
                type="number"
                value={ticketNumber}
                onChange={(e) => setTicketNumber(e.target.value)}
                size="small"
                sx={{ flexGrow: 1 }}
                inputProps={{ min: 1 }}
              />
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={fetchWinner}
                disabled={loadingWinner}
                sx={isEscala11 ? escala11ButtonStyleActive : {}}
              >
                {loadingWinner ? <CircularProgress size={24} /> : "Buscar"}
              </Button>
            </Box>
            {winnerError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {winnerError}
              </Alert>
            )}
            {winner && (
              <Box
                sx={{
                  p: 2,
                  border: "1px solid #c8e6c9",
                  borderRadius: 1,
                  bgcolor: "#e8f5e9",
                }}
              >
                <Typography variant="subtitle1" color="primary">
                  Ganhador Encontrado!
                </Typography>
                <Typography variant="body1">
                  Nome: {winner.user.name}
                </Typography>
                <Typography variant="body2">
                  Email: {winner.user.email}
                </Typography>
                <Typography variant="body2">
                  Telefone: {winner.user.phone}
                </Typography>
                <Typography variant="body2">
                  Cidade: {winner.user.city.name} - {winner.user.city.uf.uf}
                </Typography>
                <Typography variant="body2">
                  Bilhete Ganhador: {winner.ticket.number}
                </Typography>
              </Box>
            )}
            {/* --- Fim da Seção de Busca do Ganhador --- */}
          </Box>
        )}

        {currentTab === 1 && ( // Aba de Participantes
          <Box sx={{ mt: 2, flexGrow: 1, overflowY: "auto", pr: 1 }}>
            {loadingParticipants ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 100,
                }}
              >
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>
                  Carregando participantes...
                </Typography>
              </Box>
            ) : participantsError ? (
              <Alert severity="error">{participantsError}</Alert>
            ) : participants.length === 0 ? (
              <Typography>
                Nenhum participante encontrado para este sorteio.
              </Typography>
            ) : (
              <>
                {participants.map((participant) => (
                  <Box
                    key={participant.userId}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: "1px solid #eee",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="subtitle1">
                      Nome: {participant.userName}
                    </Typography>
                    <Typography variant="body2">
                      Email: {participant.userEmail}
                    </Typography>
                    <Typography variant="body2">
                      Bilhetes: {participant.ticketCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Registrado em:{" "}
                      {new Date(
                        participant.participationDate
                      ).toLocaleDateString()}
                    </Typography>
                    {participant.tickets.length > 0 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        Números:{" "}
                        {participant.tickets.map((t) => t.number).join(", ")}
                      </Typography>
                    )}
                  </Box>
                ))}
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                  <Pagination
                    count={totalParticipantsPages}
                    page={participantsPage}
                    onChange={handleParticipantsPageChange}
                    color="primary"
                  />
                </Box>
              </>
            )}
          </Box>
        )}

        {/* Conteúdo da Aba de Estatísticas */}
        {currentTab === 2 && (
          <Box sx={{ mt: 2, flexGrow: 1, overflowY: "auto", pr: 1 }}>
            {loadingPrizeStats ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 100,
                }}
              >
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>
                  Carregando estatísticas...
                </Typography>
              </Box>
            ) : prizeStatsError ? (
              <Alert severity="error">{prizeStatsError}</Alert>
            ) : prizeStats ? (
              <Box
                sx={{
                  p: 2,
                  border: "1px solid #c8e6c9",
                  borderRadius: 1,
                  bgcolor: "#e8f5e9",
                }}
              >
                <Typography variant="subtitle1" color="primary" sx={{ mb: 1 }}>
                  Estatísticas do Prêmio
                </Typography>
                <Typography variant="body1">
                  Valor Base do Prêmio: {formatCurrency(prizeStats.basePrize)}
                </Typography>
                <Typography variant="body1">
                  Porcentagem de Adicional: {prizeStats.prizePercentage}%
                </Typography>
                {prizeStats.totalPrizeIncrease != null && (
                  <Typography variant="body1">
                    Valor Adicionado:
                    {formatCurrency(prizeStats.totalPrizeIncrease)}
                  </Typography>
                )}
                <Typography variant="body1">
                  Valor Atual do Prêmio:
                  {formatCurrency(prizeStats.currentPrize)}
                </Typography>
                <Typography variant="body1">
                  Contagem de Anúncios Aprovados: {prizeStats.approvedAdsCount}
                </Typography>
                <Typography variant="body1">
                  Valor Total dos Anúncios:
                  {formatCurrency(prizeStats.totalApprovedAdsValue)}
                </Typography>
                {prizeStats.averagePrizeIncreasePerAd != null && (
                  <Typography variant="body1">
                    Aumento Médio por Anúncio: R$
                    {formatCurrency(prizeStats.averagePrizeIncreasePerAd)}
                  </Typography>
                )}
                {prizeStats.projectedFinalPrize && (
                  <Typography variant="body1">
                    Prêmio Final Projetado:
                    {formatCurrency(prizeStats.projectedFinalPrize)}
                  </Typography>
                )}

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  Última Atualização:{" "}
                  {new Date(prizeStats.lastUpdated).toLocaleString()}
                </Typography>
              </Box>
            ) : (
              <Typography>
                Não foi possível carregar as estatísticas do prêmio.
              </Typography>
            )}
          </Box>
        )}
        {sorteio.role == "MASTER" && (
          <Box
            sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleOpenEditModal}
              sx={isEscala11 ? escala11ButtonStyleActive : {}}
            >
              Editar Sorteio
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleOpenConfirmDeleteDialog}
              disabled={isDeleting}
            >
              {isDeleting ? <CircularProgress size={24} /> : "Deletar Sorteio"}
            </Button>
          </Box>
        )}
        {deleteError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {deleteError}
          </Alert>
        )}

        {/* Modal de Edição */}
        {openEditModal && (
          <SorteioEditModal
            open={openEditModal}
            onClose={handleCloseEditModal}
            sorteio={sorteio}
            token={token}
          />
        )}

        {/* --- Diálogo de Confirmação de Exclusão --- */}
        <Dialog
          open={openConfirmDeleteDialog}
          onClose={handleCloseConfirmDeleteDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Confirmar Exclusão do Sorteio?"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Tem certeza que deseja <b>deletar permanentemente</b> o sorteio
              <b> {sorteio.title}</b>? Esta ação não pode ser desfeita.
            </DialogContentText>
            {deleteError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {deleteError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseConfirmDeleteDialog}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={isDeleting}
              autoFocus
            >
              {isDeleting ? <CircularProgress size={24} /> : "Deletar"}
            </Button>
          </DialogActions>
        </Dialog>
        {/* --- Fim do Diálogo de Confirmação de Exclusão --- */}
      </Box>
    </Modal>
  );
};

export default SorteioDetalhesModal;
